import BugsnagType from '@bugsnag/js'
import LinearProgress from '@material-ui/core/LinearProgress'
import TextField from '@material-ui/core/TextField'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import axios from 'axios'
import clsx from 'clsx'
import cuid from 'cuid'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../Button'
import CurrencyPrice from '../CurrencyPrice'
import MobileModal from '../MobileModal'
import { getAPIUrl } from '../api/api'
import CartFactory, { IPricingInfo } from '../api/cart'
import '../i18n'

interface IProps {
  pricingInfo: IPricingInfo
  onClose: () => void
  onSubmit: (instructions: string, files: FileUpload[]) => void
}

interface FileUpload {
  id: string
  file: File
  progress: number
  state: 'uploading' | 'finalizing' | 'done'
  uploadedId?: string
}

interface IFileResponse {
  fileId: string
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonBackground: {
      '&.mdc-button--raised:not(:disabled)': {
        backgroundColor: theme.colors.primary,
      },
    },
    buttonText: {
      color: theme.colors.primary,
    },
  })
)

function UploadArtwork(props: IProps) {
  const classes = useStyles()
  const { t } = useTranslation('PricingCalculator', {
    useSuspense: false,
  })

  const [isUploading, setIsUploading] = useState(false)
  const [files, setFiles] = useState([] as FileUpload[])
  const [skipArtwork, setSkipArtwork] = useState(false)
  const [instructions, setInstructions] = useState('')
  const [isAddingToCart, setIsAddingToCart] = useState(false)

  const canContinue =
    (files.length > 0 && files.every((f) => f.state === 'done')) || skipArtwork
  const showInstructions = canContinue
  const buttons = (
    <div className="si-padded si-not-padded-top" style={{ width: '100%' }}>
      <h5>
        {t('upload-artwork-subtotal')}:{' '}
        {props.pricingInfo && props.pricingInfo.currencyCode ? (
          <CurrencyPrice
            price={props.pricingInfo.price.price}
            currencyCode={props.pricingInfo.currencyCode}
          />
        ) : null}
      </h5>
      <Button
        data-upload-artwork-element={'add-to-cart'}
        disabled={!canContinue || isAddingToCart}
        onClick={() => {
          setIsAddingToCart(true)
          props.onSubmit(instructions, files)
        }}
        primary
      >
        {isAddingToCart
          ? t('upload-artwork-adding-to-cart')
          : t('upload-artwork-add-to-cart')}
      </Button>
    </div>
  )

  return (
    <MobileModal
      fullPage={true}
      onClose={(evt: any) => {
        evt.preventDefault()
        props.onClose()
      }}
      buttons={buttons}
      buttonBarHeight="125px"
    >
      <div className="si-text-centered">
        <div className="si-container">
          <h2>{t('upload-artwork-title')}</h2>
          {!isUploading && !skipArtwork && (
            <div>
              <input
                id="file-upload"
                className="si-visibly-hidden"
                type="file"
                onChange={(event) => {
                  const newFiles: FileUpload[] = Array.from(
                    (event.target as HTMLInputElement).files
                  ).map((f) => {
                    return {
                      id: cuid(),
                      file: f,
                      progress: 0,
                      state: 'uploading',
                    }
                  })
                  setFiles((prevFiles) => {
                    return prevFiles.concat(newFiles)
                  })
                  setIsUploading(true)

                  newFiles.map(async (f) => {
                    const urlPath = `/media/${f.file.name}?contentType=${f.file.type}`
                    return getAPIUrl(urlPath)
                      .then((mediaPath) => {
                        return axios.post<IFileResponse>(mediaPath, f.file, {
                          maxContentLength: 100 * 1024 * 1024,
                          onUploadProgress: (pe: ProgressEvent) => {
                            setFiles((prevFiles) =>
                              prevFiles.map((origFile) => {
                                if (origFile.id !== f.id) {
                                  return origFile
                                }

                                origFile.progress = (pe.loaded / pe.total) * 100

                                if (origFile.progress >= 99) {
                                  origFile.state = 'finalizing'
                                }

                                return origFile
                              })
                            )
                          },
                        })
                      })
                      .then((resp) => {
                        setFiles((prevFiles) =>
                          prevFiles.map((origFile) => {
                            if (origFile.id !== f.id) {
                              return origFile
                            }

                            origFile.state = 'done'
                            origFile.uploadedId = resp.data.fileId

                            return origFile
                          })
                        )
                      })
                  })
                }}
                // multiple
              />
              <label
                data-upload-artwork-element={'upload-file-button'}
                htmlFor="file-upload"
                className={clsx(
                  'mdc-button mdc-button--raised mdc-button--touch si-button-wide',
                  classes.buttonBackground
                )}
              >
                {t('upload-artwork-choose-file')}
              </label>
              <p
                className={clsx(
                  'si-text-button si-padded-top',
                  classes.buttonText
                )}
              >
                <a
                  onClick={() => {
                    setSkipArtwork(true)
                  }}
                  data-upload-artwork-element={'skip-upload-button'}
                >
                  {t('upload-artwork-skip')}
                </a>
              </p>
            </div>
          )}
          {isUploading && (
            <div className="si-container">
              <div className="si-padded">
                {files.map((f) => {
                  return (
                    <div className="si-padded">
                      <LinearProgress
                        variant="determinate"
                        value={f.progress}
                      />
                      <span className="si-text-body2">
                        {f.file.name}{' '}
                        {f.state === 'uploading' &&
                          t('upload-artwork-uploading')}
                        {f.state === 'finalizing' && (
                          <span className="si-text-color-redDark">
                            {t('upload-artwork-finalizing')}
                          </span>
                        )}
                        {f.state === 'done' && (
                          <span className="si-text-color-red">
                            {t('upload-artwork-done')}
                          </span>
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        {showInstructions && (
          <div className="si-container">
            <div className="si-padded">
              <h5>Instructions (optional)</h5>
              <TextField
                data-upload-artwork-element={'instructions'}
                type="textarea"
                variant="outlined"
                className="si-full-width"
                rows={6}
                multiline={true}
                placeholder={t('upload-artwork-instructions-placeholder')}
                onKeyUp={(evt) => {
                  setInstructions((evt.target as HTMLTextAreaElement).value)
                }}
              ></TextField>
            </div>
          </div>
        )}
      </div>
    </MobileModal>
  )
}

export default UploadArtwork
